/**
 * Placeholder for the grouped notification bell and route (US 18303), so the finished UI
 * can be demonstrated before the backend is wired in.
 *
 * Matches the backend integration document "Updates Feed & Notification Bell"
 * (`GetGrouped`, `UnreadCount`, `MarkReferralRead`, `MarkAllRead`, branch
 * `sprint_87_Notification_rework_BE`) field for field, including its UTC-without-Z
 * timestamps, so the swap is a flag not a rewrite.
 *
 * ── Reverting ─────────────────────────────────────────────────────────────────
 * 1. Set `USE_GROUPED_NOTIFICATIONS_FIXTURE` to false — every call routes to the real API.
 * 2. To remove entirely, delete this file and the guarded branches in `api/index.tsx`.
 *    Reverting the UI as well means restoring `<NotificationDropDown />` in
 *    `Layout/Header/index.tsx` and `<NotificationsList />` in `NotificationsContainer`.
 * ──────────────────────────────────────────────────────────────────────────────
 *
 * All names and content are synthetic. No real patient data belongs in this file.
 */

import {
  GroupedNotificationEntry,
  GroupedNotificationsRequest,
  GroupedNotificationsResponse,
  MarkAllReadResponse,
  MarkReferralReadResponse,
  UnreadCountResponse,
} from '../types/grouped'

// ─── Demo controls ────────────────────────────────────────────────────────────

/** Master switch. False routes everything to the real API. */
export const USE_GROUPED_NOTIFICATIONS_FIXTURE = false

/** Which state to render — `empty` exercises the no-notifications state. */
type FixtureScenario = 'populated' | 'empty'

// Asserted rather than annotated: TypeScript narrows an annotated const to its
// initializer, which would make the other branch unreachable.
const FIXTURE_SCENARIO = 'populated' as FixtureScenario

const FIXTURE_DELAY_MS = 350

/** UTC without a Z suffix, as the API sends it. */
const hoursAgo = (hours: number): string =>
  new Date(Date.now() - hours * 60 * 60 * 1000).toISOString().replace('Z', '')

const daysAgo = (days: number, atHour = 14): string => {
  const date = new Date(Date.now() - days * 24 * 60 * 60 * 1000)
  date.setHours(atHour, 30, 0, 0)
  return date.toISOString().replace('Z', '')
}

// ─── Content ──────────────────────────────────────────────────────────────────

/**
 * Referrals with activity, newest first. Read state is layered on per request by
 * `withReadState`, so a row clicked or cleared this session comes back with no unread.
 */
const GROUPED_ENTRIES: Omit<GroupedNotificationEntry, 'unreadCount'>[] = [
  {
    patientId: 48210,
    refId: 'REF-24118',
    patientName: 'Robin Clement',
    updates: 5,
    lastActivityAt: hoursAgo(1),
    summary: 'New attachment, Discussions, source hospital message',
  },
  {
    patientId: 48117,
    refId: 'REF-24090',
    patientName: 'Marcus Bell',
    updates: 3,
    lastActivityAt: hoursAgo(3),
    summary: 'Insurance authorization approved, status updated',
  },
  {
    patientId: 48098,
    refId: 'REF-24077',
    patientName: 'Aisha Thompson',
    updates: 2,
    lastActivityAt: hoursAgo(6),
    summary: 'New attachment: Lab_Results.pdf summarized',
  },
  {
    patientId: 47980,
    refId: 'REF-23994',
    patientName: 'Wade Warren',
    updates: 1,
    lastActivityAt: daysAgo(1, 16),
    summary: 'Community review completed, patient choice pending',
  },
  {
    patientId: 47955,
    refId: 'REF-23961',
    patientName: 'Priya Patel',
    updates: 4,
    lastActivityAt: daysAgo(1, 15),
    summary: 'Clinical QA regenerated, Note, Referral status',
  },
  {
    patientId: 47901,
    refId: 'REF-23940',
    patientName: 'Jane Cooper',
    updates: 1,
    lastActivityAt: daysAgo(1, 11),
    summary: 'Drug pricing added to clinical review',
  },
  {
    patientId: 47866,
    refId: 'REF-23903',
    patientName: 'Robert Fields',
    updates: 2,
    lastActivityAt: daysAgo(1, 9),
    summary: 'Referral Assigned, Referral status',
  },
  {
    patientId: 47712,
    refId: 'REF-23788',
    patientName: 'Savannah Nguyen',
    updates: 1,
    lastActivityAt: daysAgo(3, 13),
    summary: 'Referral accepted, submitted to EHR',
  },
  {
    patientId: 47690,
    refId: 'REF-23770',
    patientName: 'Guy Hawkins',
    updates: 1,
    lastActivityAt: daysAgo(3, 10),
    summary: 'Sex offender check cleared at intake',
  },
  {
    patientId: 47501,
    refId: 'REF-23612',
    patientName: 'Amanda Rios',
    updates: 3,
    lastActivityAt: daysAgo(5, 12),
    summary: 'Referral source, Referral status',
  },
]

// ─── Request handling ─────────────────────────────────────────────────────────

const delay = <T,>(value: T): Promise<T> =>
  new Promise(resolve => {
    setTimeout(() => resolve(value), FIXTURE_DELAY_MS)
  })

/** Read state lives here for the session so row clicks and Clear all visibly work. */
const readPatientIds = new Set<number>()

const withReadState = (
  entry: Omit<GroupedNotificationEntry, 'unreadCount'>,
): GroupedNotificationEntry => ({
  ...entry,
  unreadCount: readPatientIds.has(entry.patientId) ? 0 : entry.updates,
})

const unreadEntries = (): GroupedNotificationEntry[] =>
  GROUPED_ENTRIES.map(withReadState).filter(entry => entry.unreadCount > 0)

/** Stands in for `POST /api/Notification/GetGrouped`. Honours `unreadOnly`; offset-paged. */
export const getGroupedNotificationsFixture = (
  body: GroupedNotificationsRequest,
): Promise<GroupedNotificationsResponse> => {
  if (FIXTURE_SCENARIO === 'empty') {
    return delay({
      data: {
        isSuccess: true,
        message: 'success',
        totalCount: 0,
        hasData: false,
        data: { items: [], ungrouped: [] },
      },
    })
  }

  const all = body.unreadOnly ? unreadEntries() : GROUPED_ENTRIES.map(withReadState)
  const pageNumber = body.pageNumber > 0 ? body.pageNumber : 1
  const pageSize = body.pageSize > 0 ? body.pageSize : 15
  const start = (pageNumber - 1) * pageSize
  const items = all.slice(start, start + pageSize)

  return delay({
    data: {
      isSuccess: true,
      message: 'success',
      totalCount: all.length,
      hasData: items.length > 0,
      data: { items, ungrouped: [] },
    },
  })
}

/** Stands in for `GET /api/Notification/UnreadCount` — referrals, not events. */
export const getGroupedUnreadCountFixture = (): Promise<UnreadCountResponse> =>
  delay({
    data: { isSuccess: true, message: 'success', data: { count: unreadEntries().length } },
  })

/** Stands in for `POST /api/Notification/MarkReferralRead`. */
export const markReferralReadFixture = (patientId: number): Promise<MarkReferralReadResponse> => {
  const entry = GROUPED_ENTRIES.find(item => item.patientId === patientId)
  const cleared = entry && !readPatientIds.has(patientId) ? entry.updates : 0
  readPatientIds.add(patientId)

  return delay({ data: { isSuccess: true, message: 'success', data: String(cleared) } })
}

/** Stands in for `POST /api/Notification/MarkAllRead`, backing the dropdown's "Clear all". */
export const markAllReadFixture = (): Promise<MarkAllReadResponse> => {
  const cleared = unreadEntries().reduce((total, entry) => total + entry.unreadCount, 0)
  GROUPED_ENTRIES.forEach(entry => readPatientIds.add(entry.patientId))
  return delay({ data: { isSuccess: true, message: 'success', data: String(cleared) } })
}
